import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse, PaginatedResponse } from '../api-types';
import type { InviteDetails, PendingInvitation } from '@welynk/types';

// ─── Types ────────────────────────────────────────────────

export type OrganizationType = 'COMPANY' | 'COLLEGE';

export type OrganizationMemberRole =
  | 'OWNER'
  | 'ADMIN'
  | 'HOD'
  | 'HIRING_MANAGER'
  | 'RECRUITER'
  | 'INTERVIEWER'
  | 'PLACEMENT_OFFICER'
  | 'INTERNAL_FACULTY'
  | 'EXTERNAL_FACULTY'
  | 'STAFF'
  | 'STUDENT'
  | 'MEMBER';

export type VerificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  type: OrganizationType;
  ownerId: string;
  logoUrl: string | null;
  website: string | null;
  description: string | null;
  /** GSTIN for B2B tax invoices (input tax credit) — null when not provided */
  gstin: string | null;
  verificationStatus: VerificationStatus;
  verifiedAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  companyProfile?: CompanyProfile | null;
  collegeProfile?: CollegeProfile | null;
  _count?: { members: number };
}

export interface CompanyProfile {
  id: string;
  organizationId: string;
  industry: string | null;
  companySize: string | null;
  headquarters: string | null;
  foundedYear: number | null;
  linkedinUrl: string | null;
  glassdoorUrl: string | null;
  careerPageBanner: string | null;
  careerPageTagline: string | null;
  culture: string | null;
}

export interface CollegeProfile {
  id: string;
  organizationId: string;
  universityType: string | null;
  affiliation: string | null;
  location: string | null;
  establishedYear: number | null;
  naacGrade: string | null;
  totalStudents: number | null;
  emailDomain: string | null;
  gradingSystem: string | null;
  semesterSystem: string | null;
  academicYearStartMonth: number | null;
  /** College sign-in provider (Google Workspace / Microsoft Entra), if enabled. */
  ssoProvider?: 'GOOGLE' | 'MICROSOFT' | null;
  ssoTenantId?: string | null;
  /** Where college emails reach students: personal sign-in email or linked college email. */
  studentEmailTarget?: 'PERSONAL' | 'COLLEGE';
  setupCompletedAt?: string | null;
}

export interface OrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  role: OrganizationMemberRole;
  status: string;
  joinType: string;
  joinedAt: string | null;
  // Present on responses from the members/pending endpoints; optional so
  // fixtures and older callers don't have to construct them.
  invitedAt?: string | null;
  createdAt?: string;
  user?: {
    id: string;
    email?: string;
    /** PENDING until an invited member accepts and sets a password. */
    status?: string;
    careerProfile?: {
      fullName: string | null;
      avatarUrl: string | null;
      headline: string | null;
    } | null;
    /** Fallback name source for imported members with no career profile yet. */
    profile?: {
      fullName: string | null;
    } | null;
  };
  /** A student's current batch in this college (a join link places them before approval). */
  batch?: { id: string; name: string } | null;
}

export interface UserOrganization {
  organization: Organization;
  role: OrganizationMemberRole;
  /** Set when the member has a custom role; its permissions replace the role's defaults. */
  customRole?: { name: string; permissions: string[] } | null;
  status: string;
  joinedAt: string | null;
}

export interface Department {
  id: string;
  organizationId: string;
  name: string;
  code: string | null;
  description: string | null;
  hodUserId: string | null;
}

export interface CreateOrganizationRequest {
  name: string;
  slug: string;
  type: OrganizationType;
  logoUrl?: string;
  website?: string;
  description?: string;
  gstin?: string | null;
}

export interface InviteMemberRequest {
  email: string;
  role: OrganizationMemberRole;
  customRoleId?: string;
  name?: string;
}

export type ImportRowStatus = 'NEW' | 'EXISTING_USER' | 'ALREADY_MEMBER' | 'REINVITE' | 'ERROR';

export interface ImportRowResult {
  line: number;
  email: string;
  name: string;
  status: ImportRowStatus;
  message?: string;
}

export interface ImportSummary {
  total: number;
  toCreate: number;
  toAdd: number;
  toReinvite: number;
  skipped: number;
  errors: number;
}

/** No plan at all (allowance is zero) vs a real cap being hit — different advice. */
export type CapacityFailure = 'NO_PLAN' | 'LIMIT_REACHED';

export interface ImportPreview {
  rows: ImportRowResult[];
  summary: ImportSummary;
  seatWarning?: string;
  seatWarningReason?: CapacityFailure;
}

export interface ImportResult extends ImportPreview {
  invitesSent: number;
  emailFailures: number;
}

export type { InviteDetails };

export interface SearchOrganizationsParams {
  query?: string;
  type?: OrganizationType;
  page?: number;
  pageSize?: number;
}

// ─── Helpers ──────────────────────────────────────────────

/** Hands a downloaded blob to the browser as a file save. */
function triggerDownload(data: BlobPart, filename: string): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Service ──────────────────────────────────────────────

export class OrganizationApiService {
  static async searchOrganizations(
    params?: SearchOrganizationsParams
  ): Promise<PaginatedResponse<Organization>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.ORGANIZATIONS.SEARCH, { params });
    return response.data.data;
  }

  static async getMyOrganizations(): Promise<UserOrganization[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<UserOrganization[]>>(
      API_ENDPOINTS.ORGANIZATIONS.MY
    );
    return response.data.data;
  }

  /** Invitations waiting on the signed-in user. */
  static async getMyInvitations(): Promise<PendingInvitation[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<PendingInvitation[]>>(
      API_ENDPOINTS.ORGANIZATIONS.MY_INVITATIONS
    );
    return response.data.data;
  }

  static async acceptMyInvitation(orgId: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.ACCEPT_MY_INVITATION(orgId));
  }

  static async declineMyInvitation(orgId: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.DECLINE_MY_INVITATION(orgId));
  }

  static async getOrganization(id: string): Promise<Organization> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Organization>>(
      API_ENDPOINTS.ORGANIZATIONS.GET(id)
    );
    return response.data.data;
  }

  static async getOrganizationBySlug(slug: string): Promise<Organization> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Organization>>(
      API_ENDPOINTS.ORGANIZATIONS.GET_BY_SLUG(slug)
    );
    return response.data.data;
  }

  static async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<Organization>>(
      API_ENDPOINTS.ORGANIZATIONS.CREATE,
      data
    );
    return response.data.data;
  }

  /** Upload a PNG/JPEG/WebP logo (max 2 MB); returns the updated organization. */
  static async uploadLogo(id: string, file: File): Promise<Organization> {
    const client = getApiClient();
    const form = new FormData();
    form.append('file', file);
    const response = await client.post<ApiResponse<Organization>>(
      API_ENDPOINTS.ORGANIZATIONS.LOGO(id),
      form
    );
    return response.data.data;
  }

  static async updateOrganization(
    id: string,
    data: Partial<CreateOrganizationRequest>
  ): Promise<Organization> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<Organization>>(
      API_ENDPOINTS.ORGANIZATIONS.UPDATE(id),
      data
    );
    return response.data.data;
  }

  static async updateCompanyProfile(
    orgId: string,
    data: Partial<CompanyProfile>
  ): Promise<CompanyProfile> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<CompanyProfile>>(
      API_ENDPOINTS.ORGANIZATIONS.UPDATE_COMPANY_PROFILE(orgId),
      data
    );
    return response.data.data;
  }

  static async updateCollegeProfile(
    orgId: string,
    data: Partial<CollegeProfile>
  ): Promise<CollegeProfile> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<CollegeProfile>>(
      API_ENDPOINTS.ORGANIZATIONS.UPDATE_COLLEGE_PROFILE(orgId),
      data
    );
    return response.data.data;
  }

  static async getMembers(
    orgId: string,
    params?: { status?: string; role?: string }
  ): Promise<OrganizationMember[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<OrganizationMember[]>>(
      API_ENDPOINTS.ORGANIZATIONS.GET_MEMBERS(orgId),
      { params }
    );
    return response.data.data;
  }

  static async inviteMember(
    orgId: string,
    data: InviteMemberRequest
  ): Promise<{ member: { userId: string }; created: boolean }> {
    const client = getApiClient();
    const response = await client.post<
      ApiResponse<{ member: { userId: string }; created: boolean }>
    >(API_ENDPOINTS.ORGANIZATIONS.INVITE_MEMBER(orgId), data);
    return response.data.data;
  }

  // ─── Roster import / export ─────────────────────────────

  /** Dry run — validates the file and reports per-row outcomes without writing. */
  static async previewImport(
    orgId: string,
    file: File,
    role: OrganizationMemberRole = 'STUDENT'
  ): Promise<ImportPreview> {
    const client = getApiClient();
    const form = new FormData();
    form.append('file', file);
    form.append('role', role);
    const response = await client.post<ApiResponse<ImportPreview>>(
      API_ENDPOINTS.ORGANIZATIONS.IMPORT_MEMBERS_PREVIEW(orgId),
      form
    );
    return response.data.data;
  }

  static async importMembers(
    orgId: string,
    file: File,
    role: OrganizationMemberRole = 'STUDENT'
  ): Promise<ImportResult> {
    const client = getApiClient();
    const form = new FormData();
    form.append('file', file);
    form.append('role', role);
    const response = await client.post<ApiResponse<ImportResult>>(
      API_ENDPOINTS.ORGANIZATIONS.IMPORT_MEMBERS(orgId),
      form
    );
    return response.data.data;
  }

  static async downloadImportTemplate(
    orgId: string,
    format: 'csv' | 'xlsx' = 'xlsx'
  ): Promise<void> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.ORGANIZATIONS.IMPORT_TEMPLATE(orgId), {
      params: { format },
      responseType: 'blob',
    });
    triggerDownload(response.data, `welynk-student-import-template.${format}`);
  }

  static async exportMembers(
    orgId: string,
    params: { role?: string; status?: string; format?: 'csv' | 'xlsx' } = {}
  ): Promise<void> {
    const client = getApiClient();
    const format = params.format ?? 'xlsx';
    const response = await client.get(API_ENDPOINTS.ORGANIZATIONS.EXPORT_MEMBERS(orgId), {
      params: { ...params, format },
      responseType: 'blob',
    });
    const stamp = new Date().toISOString().slice(0, 10);
    triggerDownload(response.data, `students-${stamp}.${format}`);
  }

  // ─── Invite lifecycle ───────────────────────────────────

  static async resendInvite(orgId: string, userId: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.RESEND_INVITE(orgId, userId));
  }

  static async revokeInvite(orgId: string, userId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.ORGANIZATIONS.REVOKE_INVITE(orgId, userId));
  }

  static async getInvite(token: string): Promise<InviteDetails> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<InviteDetails>>(
      API_ENDPOINTS.ORGANIZATIONS.GET_INVITE(token)
    );
    return response.data.data;
  }

  /** Accepts, with the token alone, an invitation that belongs to an existing account. */
  static async acceptInvite(token: string): Promise<{ userId: string; orgId: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ userId: string; orgId: string }>>(
      API_ENDPOINTS.ORGANIZATIONS.ACCEPT_INVITE(token),
      {}
    );
    return response.data.data;
  }

  /** Joining with a new account, step 1: send a code to the personal email. */
  static async startInviteSignup(
    token: string,
    email: string
  ): Promise<{ otpId: string; expiresIn: number; email: string }> {
    const client = getApiClient();
    const response = await client.post<
      ApiResponse<{ otpId: string; expiresIn: number; email: string }>
    >(API_ENDPOINTS.ORGANIZATIONS.INVITE_SIGNUP(token), { email });
    return response.data.data;
  }

  /** Step 2: the code, with the name and password for the new account. */
  static async completeInviteSignup(
    token: string,
    data: { otpId: string; code: string; fullName: string; password: string }
  ): Promise<{ email: string; orgId: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ email: string; orgId: string }>>(
      API_ENDPOINTS.ORGANIZATIONS.INVITE_SIGNUP_VERIFY(token),
      data
    );
    return response.data.data;
  }

  /** Accepts as the signed-in user; an invite to an email with no account joins this one. */
  static async claimInvite(token: string): Promise<{ orgId: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ orgId: string }>>(
      API_ENDPOINTS.ORGANIZATIONS.CLAIM_INVITE(token)
    );
    return response.data.data;
  }

  static async requestToJoin(orgId: string, role: string): Promise<OrganizationMember> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<OrganizationMember>>(
      API_ENDPOINTS.ORGANIZATIONS.JOIN(orgId),
      { role }
    );
    return response.data.data;
  }

  /** `batchId` places a college student in a batch as part of approving them. */
  static async approveMember(orgId: string, userId: string, batchId?: string): Promise<void> {
    const client = getApiClient();
    await client.post(
      API_ENDPOINTS.ORGANIZATIONS.APPROVE_MEMBER(orgId, userId),
      batchId ? { batchId } : undefined
    );
  }

  /** The owner hands the organization to an active admin (confirmed with their password). */
  static async transferOwnership(
    orgId: string,
    newOwnerUserId: string,
    password: string
  ): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.TRANSFER_OWNERSHIP(orgId), {
      newOwnerUserId,
      password,
    });
  }

  static async rejectMember(orgId: string, userId: string, reason?: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.REJECT_MEMBER(orgId, userId), { reason });
  }

  static async leaveOrganization(orgId: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.ORGANIZATIONS.LEAVE(orgId));
  }

  static async getDepartments(orgId: string): Promise<Department[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<Department[]>>(
      API_ENDPOINTS.ORGANIZATIONS.GET_DEPARTMENTS(orgId)
    );
    return response.data.data;
  }

  static async updateDepartment(
    orgId: string,
    deptId: string,
    data: {
      name?: string;
      code?: string;
      description?: string;
      hodUserId?: string | null;
      orgUnitId?: string | null;
    }
  ): Promise<Department> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<Department>>(
      API_ENDPOINTS.ORGANIZATIONS.UPDATE_DEPARTMENT(orgId, deptId),
      data
    );
    return response.data.data;
  }

  static async deleteDepartment(orgId: string, deptId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.ORGANIZATIONS.DELETE_DEPARTMENT(orgId, deptId));
  }

  static async createDepartment(
    orgId: string,
    data: {
      name: string;
      code?: string;
      description?: string;
      hodUserId?: string;
      orgUnitId?: string | null;
    }
  ): Promise<Department> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<Department>>(
      API_ENDPOINTS.ORGANIZATIONS.CREATE_DEPARTMENT(orgId),
      data
    );
    return response.data.data;
  }

  static async updateMemberRole(
    orgId: string,
    userId: string,
    role: OrganizationMemberRole
  ): Promise<OrganizationMember> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<OrganizationMember>>(
      API_ENDPOINTS.ORGANIZATIONS.UPDATE_MEMBER_ROLE(orgId, userId),
      { role }
    );
    return response.data.data;
  }

  static async removeMember(orgId: string, userId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.ORGANIZATIONS.REMOVE_MEMBER(orgId, userId));
  }

  static async getPendingMembers(orgId: string): Promise<OrganizationMember[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<OrganizationMember[]>>(
      API_ENDPOINTS.ORGANIZATIONS.GET_PENDING(orgId)
    );
    return response.data.data;
  }
}
